import React, { createContext, useContext } from 'react';
import { create } from 'zustand';

import { ProjectPermissionsData } from '@/src/components/project/api/getProjectPermissions';

export type TagType = {
  id: string | number;
  name: string;
  color?: string;
  isSystem?: boolean;
  version?: number;
};

export type RowTagsMap = { [rowId: string]: TagType[] };

export type ShowAllTagsMap = { [rowId: string]: boolean };

export type FileTagsMap = { [inboxDocumentId: string]: TagType[] };

export type TagsStore = {
  tags: TagType[];
  rowTags: RowTagsMap;
  fileTags?: FileTagsMap;
  permissions?: ProjectPermissionsData;
  hasFullTagsAccess?: boolean;
  setTags: (tags: TagType[]) => void;
  setRowTags: (rowTags: RowTagsMap) => void;
  updateRowTags: (rowId: string, tags: TagType[]) => void;
  setFileTags?: (inboxDocumentId: string, tags: TagType[]) => void;
  setHasFullTagsAccess?: (hasAccess: boolean) => void;
  setPermissions?: (permissions: ProjectPermissionsData) => void;
};

export const useTagsStore = create<TagsStore>((set) => ({
  tags: [],
  rowTags: {},
  fileTags: {},
  permissions: undefined,
  hasFullTagsAccess: false,
  setTags: (tags) => set({ tags }),
  setRowTags: (rowTags) => set({ rowTags }),
  updateRowTags: (rowId, tags) =>
    set((state) => ({ rowTags: { ...state.rowTags, [rowId]: tags } })),
  setFileTags: (inboxDocumentId, tags) =>
    set((state) => ({ fileTags: { ...state.fileTags, [inboxDocumentId]: tags } })),
  setHasFullTagsAccess: (hasAccess) => set({ hasFullTagsAccess: hasAccess }),
  setPermissions: (permissions) => set({ permissions }),
}));

// Create a context for the tags store, including permissions
export type TagsStoreContextValue = {
  store: ReturnType<typeof useTagsStore>;
  permissions?: ProjectPermissionsData;
};

const TagsStoreContext = createContext<TagsStoreContextValue | null>(null);

export const TagsContextProvider: React.FC<{
  children: React.ReactNode;
  permissions?: ProjectPermissionsData;
}> = ({ children, permissions }) => {
  const store = useTagsStore();
  return (
    <TagsStoreContext.Provider value={{ store, permissions }}>{children}</TagsStoreContext.Provider>
  );
};

export const useTagsStoreContext = () => {
  const context = useContext(TagsStoreContext);
  if (!context) {
    throw new Error('useTagsStoreContext must be used within a TagsContextProvider');
  }
  return context;
};
